// useMapViews.js — saved-view fly-to/create/select/update-bbox state (ticket 09 extraction).
// Independent of useSymbolDragDrop; both were pulled out of MapEditorModalInner in the same
// ticket since they're small, self-contained concerns.

import { useCallback, useEffect, useRef, useState } from 'react';
import { transformExtent } from 'ol/proj';
import { extractMapState, resolveViewBbox, resolveViewFitPadding } from '../../schema/mapPmAdapter';
import { DEFAULT_VIEW_ID, mapSchema, newViewId as _newViewId } from '../../schema/mapSchema';
import { bboxFromOlMapRect, viewportBboxNested } from './mapGeometryUtils';

// Locate the first `map_view` node with the given viewId; returns its pos or null.
function _findViewPos(doc, viewId) {
    let found = null;
    doc.descendants((node, pos) => {
        if (found !== null) return false;
        if (node.type.name === 'map_view' && node.attrs.viewId === viewId) {
            found = pos;
            return false;
        }
    });
    return found;
}

// map-editor-url-state ticket 04: how far the live crop frame's bbox may drift from the selected
// named view's stored bbox before it counts as "diverged" (see the moveend-tracking effect
// below). Expressed in screen pixels (converted to map units via the view's current resolution,
// which varies with zoom) rather than a fixed geo distance — a fixed degree/meter epsilon would
// be far too tight when zoomed out and far too loose when zoomed in. A couple of pixels absorbs
// floating-point/animation-easing-endpoint rounding without masking any real, visible pan/zoom.
const DIVERGENCE_TOLERANCE_PX = 2;

// Nested `[[sw],[ne]]` lon/lat bboxes -> true if they land within `DIVERGENCE_TOLERANCE_PX`
// pixels of each other at the given resolution (map units per pixel), compared in the projected
// (EPSG:3857) space the map itself renders in — matches how the crop-frame accuracy fix above
// reasons about the frame's own pixel size. An unknown resolution (map not yet sized) is treated
// as "matching" rather than "diverged" — there's nothing meaningful to compare yet, and we'd
// rather stay silent than flag a false positive.
function bboxesMatch(a, b, resolution) {
    if (!resolution) return true;
    const [aMinX, aMinY, aMaxX, aMaxY] = transformExtent([a[0][0], a[0][1], a[1][0], a[1][1]], 'EPSG:4326', 'EPSG:3857');
    const [bMinX, bMinY, bMaxX, bMaxY] = transformExtent([b[0][0], b[0][1], b[1][0], b[1][1]], 'EPSG:4326', 'EPSG:3857');
    const threshold = resolution * DIVERGENCE_TOLERANCE_PX;
    return Math.abs(aMinX - bMinX) <= threshold && Math.abs(aMinY - bMinY) <= threshold
        && Math.abs(aMaxX - bMaxX) <= threshold && Math.abs(aMaxY - bMaxY) <= threshold;
}

export default function useMapViews({
    mapRef, stateRef, vignetteFrameRef, mapReady, initialViewId, bboxOverride, createViewFromBbox, updateViewBbox, currentDtg,
}) {
    const [selectedViewId, setSelectedViewId] = useState(initialViewId);
    // map-editor-url-state ticket 04/06: the live crop frame's bbox — while a named view is
    // selected, only set while it's diverged (beyond tolerance) from that view's own stored
    // bbox; while no view is selected ("All markings"), unconditionally mirrors the current
    // camera position instead (see the tracking effect below for why the two cases differ).
    // `null` whenever there's nothing worth reflecting. MapEditorModal forwards this to
    // `onBboxChange`, which MapEditorPortal mirrors into the `mapBbox` URL param.
    const [divergedBbox, setDivergedBbox] = useState(null);

    // Keeps selectedViewId in sync when the modal is reopened targeting a different view
    // (initialViewId prop change), same as the pre-extraction effect in MapEditorModalInner.
    useEffect(() => { setSelectedViewId(initialViewId); }, [initialViewId]);

    // `animate` defaults to true for manual "jump to view"/"fit all shapes" clicks (matches the
    // pre-existing 600ms fly); the initial-open snap below passes `{ animate: false }` so the map
    // doesn't visibly pan/zoom from the world default right as the modal appears.
    //
    // Bug fix (all-markings-dtg-crop): forwards `currentDtg` (the live editor's DTG scrub
    // position — MapEditorModal's `dtgRange.valueMs`) to `resolveViewBbox` so the "All markings"
    // fallback bbox resolves each marking's position AT the currently-scrubbed DTG (see
    // resolveViewBbox/computeDefaultViewBbox's doc comments) rather than each marking's
    // latest-ever position. Named views with their own stored bbox are unaffected either way.
    //
    // map-editor-url-state ticket 04: a named view's stored `bbox` is captured off the *vignette
    // crop frame* (see `bboxFromOlMapRect`/`handleUpdateViewBbox` below), a 16:9 sub-rect that's
    // generally SMALLER than the full OL container (`VignetteOverlay.jsx` letterboxes it to fit
    // 16:9 within whatever shape the editor pane happens to be). Fitting the *whole container* to
    // that bbox with zero padding — what this used to do — makes the crop frame show MORE geo
    // area than was actually saved, since the frame is a strict sub-rect of the fitted container.
    // Named views instead compute center/resolution directly from the frame's own pixel size, so
    // the crop frame itself (not the container) lands exactly on the stored bbox. Falls back to
    // the old container-fit behavior if the frame hasn't measured yet (e.g. the very first paint,
    // before VignetteOverlay's ResizeObserver has fired) — approximate is better than a crash.
    //
    // `flyOverride` (only ever passed by the initial-open effect below, via `bboxOverride`) lets
    // a `mapBbox` URL deep link land the crop frame on that exact bbox instead of whatever the
    // view itself would otherwise resolve to — a named view's own stored bbox, OR (map-editor-
    // url-state ticket 06) the ad-hoc "fit all markings" bbox when no view is selected. Always
    // takes the crop-frame-accurate center/resolution path below (same as a named view), since an
    // override is by definition an exact target bbox to honor, never the padded/approximate
    // "fit whatever's currently in the doc" case.
    const flyToView = useCallback((vid, { animate = true, flyOverride } = {}) => {
        const map = mapRef.current;
        const state = stateRef.current;
        if (!map || !state) return;
        const { markings, layers: ls, views: vs } = extractMapState(state.doc);
        const fitPadding = resolveViewFitPadding(vid);
        const bbox = flyOverride || resolveViewBbox(vid, vs, markings, ls, currentDtg);
        if (!bbox) return;
        const [[w, s], [e, n]] = bbox;
        const duration = animate ? 600 : 0;
        const frame = vignetteFrameRef.current;
        const wantsExactFit = fitPadding === 0 || !!flyOverride;

        if (wantsExactFit && frame && frame.width > 0 && frame.height > 0) {
            const [minX, minY, maxX, maxY] = transformExtent([w, s, e, n], 'EPSG:4326', 'EPSG:3857');
            const center = [(minX + maxX) / 2, (minY + maxY) / 2];
            // Both dimensions should agree in principle (the stored bbox was itself captured off
            // a 16:9 frame in the same projection) — max() is a small safety margin against fp
            // drift so the full saved extent always ends up fully visible within the frame,
            // never clipped on either axis.
            const resolution = Math.max((maxX - minX) / frame.width, (maxY - minY) / frame.height);
            map.getView().animate({ center, resolution, duration });
            return;
        }

        const padding = [fitPadding, fitPadding, fitPadding, fitPadding];
        map.getView().fit(transformExtent([w, s, e, n], 'EPSG:4326', 'EPSG:3857'), { padding, duration });
    }, [mapRef, stateRef, currentDtg, vignetteFrameRef]);

    // One-shot: snap to the initially-selected view (or fit all markings) as soon as the map is
    // ready, so the editor opens already zoomed to the relevant content instead of the world
    // default set by useOlMapLifecycle. Guarded by a ref rather than depending only on `mapReady`
    // so it never re-fires on a later selectedViewId change (that's handleSelectView's job, with
    // its animated fly).
    const hasFlownOnOpenRef = useRef(false);
    useEffect(() => {
        if (!mapReady || hasFlownOnOpenRef.current) return;
        hasFlownOnOpenRef.current = true;
        flyToView(selectedViewId, { animate: false, flyOverride: bboxOverride });
    }, [mapReady, selectedViewId, flyToView, bboxOverride]);

    // map-editor-url-state ticket 06: while no named view is selected ("All markings"), the very
    // first camera settle of the whole session is this same one-shot open-time fit-all-markings
    // fly above, not a real user gesture — skip reporting THAT one (mount-scoped, never reset)
    // so a fresh no-view open starts with a clean `mapBbox`. Tracks "has ANY settle happened yet
    // this session" (not "has a no-view settle happened yet") so that if the session instead
    // opens on a NAMED view, its first settle still consumes this — a LATER mid-session switch
    // TO "All markings" is deliberately NOT skipped; its own resulting settle reports
    // immediately, same treatment a switch to a named view already gets.
    const hasHadFirstSettleRef = useRef(false);

    // map-editor-url-state ticket 04/06: live camera-position tracking, throttled off the same
    // `moveend` cadence `useViewportBboxTracking` uses (~200ms) — `moveend` only fires once a
    // pan/zoom *gesture* actually settles (including the end of an animated `flyToView`/`fit`),
    // it's never a continuous mid-drag stream, so this is the same "discrete settle points, not
    // every frame" shape as the window-setting handlers' live writes, not the dtg-scrub-style
    // churn we deliberately kept out of the URL.
    //
    // Two different meanings depending on whether a named view is selected:
    //   - Named view: `divergedBbox` is the live crop frame's bbox, but ONLY while it's diverged
    //     (beyond tolerance) from that view's own stored bbox — `null` once they match again.
    //   - No view ("All markings", ticket 06): there's no stored bbox to diverge FROM — that
    //     baseline is itself a moving target (`resolveViewBbox`'s fit-all-content bbox shifts as
    //     markings/layers change, independent of any panning), so "diverged from it" isn't a
    //     meaningful signal here. Instead `divergedBbox` unconditionally mirrors wherever the
    //     crop frame currently sits, on every settle — this is just "where is the camera right
    //     now", not a divergence check — except the session's very first settle (see the ref
    //     above), which is the automatic open-time fit-all rather than a real user gesture.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return;
        const isNamedView = resolveViewFitPadding(selectedViewId) === 0;

        let throttleTimer = null;
        const checkPosition = () => {
            const state = stateRef.current;
            const frame = vignetteFrameRef.current;
            if (!state || !frame || frame.width <= 0 || frame.height <= 0) return;
            const currentBbox = bboxFromOlMapRect(map, frame);
            if (!currentBbox) return;
            const isFirstSettleEver = !hasHadFirstSettleRef.current;
            hasHadFirstSettleRef.current = true;

            if (!isNamedView) {
                if (isFirstSettleEver) return;
                setDivergedBbox(currentBbox);
                return;
            }

            const { markings, layers: ls, views: vs } = extractMapState(state.doc);
            const storedBbox = resolveViewBbox(selectedViewId, vs, markings, ls, currentDtg);
            if (!storedBbox) return;
            const resolution = map.getView().getResolution();
            setDivergedBbox(bboxesMatch(currentBbox, storedBbox, resolution) ? null : currentBbox);
        };
        const throttledCheck = () => {
            clearTimeout(throttleTimer);
            throttleTimer = setTimeout(checkPosition, 200);
        };
        map.on('moveend', throttledCheck);
        return () => { map.un('moveend', throttledCheck); clearTimeout(throttleTimer); };
    }, [mapRef, mapReady, selectedViewId, currentDtg, vignetteFrameRef, stateRef]);

    const handleCreateView = useCallback((name) => {
        const map = mapRef.current;
        const bbox = bboxFromOlMapRect(map, vignetteFrameRef.current);
        if (!bbox) return;
        const viewId = createViewFromBbox(name, bbox);
        if (viewId) {
            setSelectedViewId(viewId);
            // The just-created view's stored bbox IS this same current crop, by construction —
            // clear immediately rather than waiting for a `moveend` that may never come (no
            // camera movement happens on create).
            setDivergedBbox(null);
        }
    }, [mapRef, vignetteFrameRef, createViewFromBbox]);

    const handleSelectView = useCallback((id) => {
        setSelectedViewId(id);
        // Clears any stale divergence flag from whichever view was previously selected — the
        // upcoming animated fly's own `moveend` re-evaluates it against the new view once it
        // settles, but without this the old (now-irrelevant) value would linger on screen/in the
        // URL for the ~600ms the fly takes.
        setDivergedBbox(null);
        flyToView(id);
    }, [flyToView]);

    const handleUpdateViewBbox = useCallback((viewId) => {
        const map = mapRef.current;
        const bbox = bboxFromOlMapRect(map, vignetteFrameRef.current);
        if (bbox) {
            updateViewBbox(viewId, bbox);
            // Same reasoning as handleCreateView above — the view's stored bbox now IS this
            // current crop, with no camera movement to trigger a re-check via `moveend`.
            if (viewId === selectedViewId) setDivergedBbox(null);
        }
    }, [mapRef, vignetteFrameRef, updateViewBbox, selectedViewId]);

    // Stable ref so captureViewportOnClose can read the current selectedViewId without
    // becoming a stale closure (the capture happens on close, after possible view switches).
    const selectedViewIdRef = useRef(selectedViewId);
    useEffect(() => { selectedViewIdRef.current = selectedViewId; }, [selectedViewId]);

    const captureViewportOnClose = useCallback(() => {
        const map = mapRef.current;
        const state = stateRef.current;
        if (!map || !state) return { viewId: null, newState: null };

        // Prefer the vignette-constrained bbox (matches how named views are created/updated
        // by the manual controls); fall back to the full OL viewport if the vignette frame
        // hasn't been measured yet (e.g. the user closed the editor before the first rAF).
        const bbox = bboxFromOlMapRect(map, vignetteFrameRef.current)
            ?? viewportBboxNested(map);
        if (!bbox) return { viewId: null, newState: null };

        const vid = selectedViewIdRef.current;
        if (!vid || vid === DEFAULT_VIEW_ID) {
            // "All markings" — create a new view node directly in the PM doc so the flush
            // can use this updated state synchronously (no React-state dispatch needed here).
            const newViewId = _newViewId();
            const viewNode = mapSchema.nodes.map_view.create({
                viewId: newViewId, name: 'Custom view', bbox,
            });
            const tr = state.tr.insert(state.doc.content.size, viewNode);
            const newState = state.apply(tr);
            return { viewId: newViewId, newState };
        }

        // Named view — update its bbox node directly in the PM doc.
        const pos = _findViewPos(state.doc, vid);
        if (pos == null) return { viewId: vid, newState: null };
        const node = state.doc.nodeAt(pos);
        const tr = state.tr.setNodeMarkup(pos, null, { ...node.attrs, bbox });
        const newState = state.apply(tr);
        return { viewId: vid, newState };
    }, [mapRef, stateRef, vignetteFrameRef]);

    return { selectedViewId, setSelectedViewId, divergedBbox, flyToView, handleCreateView, handleSelectView, handleUpdateViewBbox, captureViewportOnClose };
}
